/**
 * Option lists shared by the list-page filter bars.
 *
 * Priority means the same four bands on clients, leads and tasks, and "within N
 * days" means the same four windows wherever it appears, so both are declared once.
 * A page that needed its own wording would be saying the filter means something
 * different there — which is exactly the drift this file exists to prevent.
 */
import type { FilterOption } from '../components/FilterBar'

/** Client, lead and task priority — the same four values in all three tables. */
export const PRIORITY_FILTER_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

/**
 * Forward-looking windows, in days. The backend caps these at 365
 * (`crm_filters.MAX_EXPIRY_WINDOW_DAYS`); anything longer is a 422, so the list here
 * must not offer one.
 */
export const EXPIRY_WINDOW_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any expiry date' },
  { value: '30', label: 'Expires in 30 days' },
  { value: '60', label: 'Expires in 60 days' },
  { value: '90', label: 'Expires in 90 days' },
  { value: '180', label: 'Expires in 180 days' },
  { value: '365', label: 'Expires in a year' },
]

/** The same windows for a task's due date, worded as a due date rather than an expiry. */
export const DUE_WINDOW_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any due date' },
  { value: '1', label: 'Due today' },
  { value: '7', label: 'Due in 7 days' },
  { value: '30', label: 'Due in 30 days' },
  { value: '90', label: 'Due in 90 days' },
]

/**
 * Review state on the ownership change log.
 *
 * Three states in one control, replacing a "Show reviewed too" checkbox that could
 * only say two of them. Unreviewed is the neutral value because that is the queue —
 * the page exists to work through what has not been looked at.
 */
export const REVIEW_STATE_OPTIONS: FilterOption[] = [
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'all', label: 'All changes' },
]

/** What changed on the license. Mirrors the API's `change_type` values. */
export const CHANGE_TYPE_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any change' },
  { value: 'entity_name', label: 'Name change' },
  { value: 'frn', label: 'FRN change' },
  { value: 'both', label: 'Name + FRN' },
]

/**
 * Backward-looking windows — how recently a change was detected. The forward-looking
 * lists above are capped at 365 by the CRM filter service; this one is not, because
 * the change log is a growing history and "everything in the last two years" is a
 * reasonable question to ask of it. The API caps it at 3650.
 */
export const DETECTED_WINDOW_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
]

/** Turn a filter-bar string back into the number the API wants, or undefined. */
export function windowDays(value: string): number | undefined {
  if (!value) return undefined
  const n = parseInt(value, 10)
  return Number.isFinite(n) && n > 0 ? n : undefined
}
